import type { IconName } from './icons';

/**
 * «خطوات البداية»: أول شغل فعلى بعد إعداد الشركة. كل خطوة تُستنتج من بيانات الشركة
 * نفسها (عميل موجود = الخطوة تمت)، فلا شىء يُعلَّم يدويًا ولا يتعارض مع الحقيقة.
 */
export type StartView = 'customers' | 'catalog' | 'invoices' | 'suppliers' | 'expenses' | 'members';

export interface StartCounts {
  customers: number | null;
  items: number | null;
  invoices: number | null;
  suppliers: number | null;
  expenses: number | null;
  members: number | null;
}

export interface StartStep {
  id: StartView;
  title: string;
  hint: string;
  icon: IconName;
  done: boolean;
}

const DEFINITIONS: ReadonlyArray<
  Omit<StartStep, 'done'> & { key: keyof StartCounts; min: number }
> = [
  {
    id: 'customers',
    key: 'customers',
    min: 1,
    title: 'أضف أول عميل',
    hint: 'الاسم والتليفون كفاية — الباقى تكمّله بعدين.',
    icon: 'users',
  },
  {
    id: 'catalog',
    key: 'items',
    min: 1,
    title: 'سجّل صنف أو خدمة',
    hint: 'اللى بتبيعه بسعره، عشان الفاتورة تتملى لوحدها.',
    icon: 'tag',
  },
  {
    id: 'invoices',
    key: 'invoices',
    min: 1,
    title: 'اعمل أول فاتورة مبيعات',
    hint: 'القيد بيترحّل لوحده، ومديونية العميل بتظهر فورًا.',
    icon: 'invoice',
  },
  {
    id: 'suppliers',
    key: 'suppliers',
    min: 1,
    title: 'أضف أول مورد',
    hint: 'عشان تتابع اللى عليك وتسدّد فى ميعاده.',
    icon: 'truck',
  },
  {
    id: 'expenses',
    key: 'expenses',
    min: 1,
    title: 'سجّل أول مصروف',
    hint: 'إيجار، كهربا، نثريات — من الخزينة أو البنك.',
    icon: 'expense',
  },
  {
    id: 'members',
    key: 'members',
    // المالك نفسه عضو، فالخطوة تتم بعضو ثانٍ
    min: 2,
    title: 'ادعُ المحاسب',
    hint: 'بصلاحيات المحاسب، من غير ما تديله كلمة سرك.',
    icon: 'users',
  },
];

/** الخطوات الظاهرة: ما لا يُعرف عدده (لا صلاحية لقراءته) لا يُعرض أصلاً. */
export function startSteps(counts: StartCounts): StartStep[] {
  return DEFINITIONS.filter((definition) => counts[definition.key] !== null).map(
    ({ key, min, ...step }) => ({ ...step, done: (counts[key] ?? 0) >= min }),
  );
}

export function startProgress(steps: readonly StartStep[]): { done: number; total: number } {
  return { done: steps.filter((step) => step.done).length, total: steps.length };
}

export function startDismissedKey(companyId: string): string {
  return 'hesabat.start.dismissed.' + companyId;
}
